/**
 * AISummaryCard — VESL-style AI summary with collapsible sections.
 */

import React, { useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { useTheme } from "@mui/material/styles";
import { sanitizeAiSummaryHtml } from "./aiSummaryHtml";

interface AISummaryCardProps {
  marketplaceAnalysis: string | null;
  trendAnalysis: string | null;
  isLoading: boolean;
  error: string | null;
}

const AISummaryCard: React.FC<AISummaryCardProps> = ({
  marketplaceAnalysis,
  trendAnalysis,
  isLoading,
  error,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const [expanded, setExpanded] = useState(false);
  const hasContent = marketplaceAnalysis || trendAnalysis;

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 3,
        border: `1px solid ${theme.palette.divider}`,
        overflow: "hidden",
        position: "relative",
      }}
    >
      {/* Card header — matching VESL .card-header */}
      <Box
        sx={{
          px: 2,
          py: 1.5,
          borderBottom: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Typography
          variant="h6"
          sx={{
            m: 0,
            color: "#2c78d4",
            fontWeight: 600,
          }}
        >
          AI Summary
          {isLoading && (
            <Chip
              label="Generating..."
              size="small"
              variant="outlined"
              color="primary"
              sx={{
                ml: 1,
                height: 22,
                fontSize: "0.7rem",
              }}
            />
          )}
        </Typography>
      </Box>

      {/* Content — collapsible like VESL */}
      <Box
        sx={{
          px: 2,
          py: 1.5,
          maxHeight: expanded ? 2000 : 200,
          overflow: "hidden",
          transition: "max-height 0.3s ease",
          pb: hasContent ? 6 : 1.5,
          // Base prose follows the theme so it's readable in dark mode; the
          // AI HTML is sanitized (sanitizeAiSummaryHtml) to drop hardcoded
          // text colors, keeping only the green/red delta spans.
          color: "text.primary",
          /* Hide "Executive Summary" headers from AI response */
          "& h1, & h2, & h3": { display: "none" },
        }}
      >
        {isLoading && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              py: 2,
            }}
          >
            <CircularProgress size={20} thickness={4} />
            <Typography variant="body2" color="text.secondary">
              Generating AI insights...
            </Typography>
          </Box>
        )}

        {error && !isLoading && (
          <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
            AI summary unavailable.
          </Typography>
        )}

        {!isLoading && !error && !hasContent && (
          <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
            AI summary will appear after data is loaded.
          </Typography>
        )}

        {!isLoading && !error && hasContent && (
          <Box>
            {trendAnalysis && (
              <Box
                sx={{
                  "& p": { my: 0.5 },
                  lineHeight: 1.7,
                  fontSize: "0.875rem",
                  mb: marketplaceAnalysis ? 2 : 0,
                }}
                dangerouslySetInnerHTML={{
                  __html: sanitizeAiSummaryHtml(trendAnalysis) ?? "",
                }}
              />
            )}
            {marketplaceAnalysis && (
              <Box
                sx={{
                  "& p": { my: 0.5 },
                  lineHeight: 1.7,
                  fontSize: "0.875rem",
                }}
                dangerouslySetInnerHTML={{
                  __html: sanitizeAiSummaryHtml(marketplaceAnalysis) ?? "",
                }}
              />
            )}
          </Box>
        )}
      </Box>

      {/* Toggle button — matching VESL .toggle-button */}
      {hasContent && !isLoading && (
        <Button
          onClick={() => setExpanded(!expanded)}
          size="small"
          sx={{
            position: "absolute",
            bottom: 8,
            left: "50%",
            transform: "translateX(-50%)",
            bgcolor: isDark ? "rgba(255,255,255,0.08)" : "#f8f9fa",
            color: isDark ? "#90caf9" : "#2c78d4",
            borderRadius: 2,
            px: 2,
            textTransform: "none",
            fontSize: "0.75rem",
            "&:hover": {
              bgcolor: isDark ? "rgba(255,255,255,0.12)" : "#e8eef6",
            },
          }}
          endIcon={expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        >
          {expanded ? "Show Less" : "Show More"}
        </Button>
      )}
    </Paper>
  );
};

export default AISummaryCard;
